import { Button, Col, Form, Input, InputNumber, Modal, Radio, Row, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from '../../page.module.scss';
import { BagMap } from '@/lib/components/bag/bagMap';
import { useEffect, useState } from 'react';
import { useFeature } from '@/lib/hooks/useFeature';
import { Relatie, OwnershipType, getRegistrationYear, hasOwnershipStatus } from '@/lib/types';
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';

export const Bedrijfsgegevens: React.FunctionComponent = () => {
  const { t } = useTranslation('input');
  const form = Form.useFormInstance<Relatie>();
  const [modalIsShown, setModalIsShown] = useState(false);
  const isBagEnabled = useFeature('show-bag-koppeling');
  const registratiejaar = Form.useWatch('registratiejaar', { form, preserve: true });
  const showOwnershipStatus = hasOwnershipStatus(registratiejaar);
  const isOwnershipStatusUnsupportedYear = Number.isFinite(getRegistrationYear(registratiejaar)) && !showOwnershipStatus;
  const requiresTotaleBeteelbareKasOppervlakte = Number(registratiejaar) >= 2026;
  const isKvkReadOnly = Number(registratiejaar) >= 2026;

  useEffect(() => {
    if (isOwnershipStatusUnsupportedYear) {
      form.setFieldsValue({
        eigendomstypeDeelnemer: undefined,
      });
    }
  }, [form, isOwnershipStatusUnsupportedYear]);

  useEffect(() => {
    if (!requiresTotaleBeteelbareKasOppervlakte) {
      form.setFields([{ name: 'totaleBeteelbareKasOppervlakte', errors: [] }]);
    }
  }, [form, requiresTotaleBeteelbareKasOppervlakte]);

  return (
    <div className={styles.companyInfo}>
      <Row gutter={[32, 16]}>
        <Col xs={24} lg={16}>
          <h2>Bedrijfsgegevens</h2>
          <Form.Item label={t('form.companyInfo.companyName')} name="bedrijfsnaam">
            <Input disabled />
          </Form.Item>
          <Form.Item
            label={t('form.companyInfo.kvkNumber')}
            name="kvkNummer"
            rules={[
              { required: !isKvkReadOnly, message: 'Het KVK-nummer is verplicht.' },
              { pattern: /^\d{8}$/, message: 'Het KVK-nummer moet precies 8 cijfers bevatten.' },
            ]}>
            <Input maxLength={8} disabled={isKvkReadOnly} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={8}>
          <h2>Bevoegd gezag</h2>
          <Form.Item label={t('form.companyInfo.localAuthority')} name="gemeente">
            <Input disabled />
          </Form.Item>
          <Form.Item label={t('form.companyInfo.localAuthorityWater')} name="waterschap">
            <Input disabled />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[32, 16]} className={styles.companyAddresses}>
        <Col xs={24} lg={8}>
          <h2>Vestigingsadres</h2>
          <p className={styles.companyAddressNote}>Aanpassingen dient u te melden bij uw bevoegd gezag.</p>
          <Form.Item label={t('form.companyInfo.uonumber')} name="uoNummer">
            <Input disabled />
          </Form.Item>
          <Form.Item label={t('form.companyInfo.vestigingsAdresStraat')} name="vestigingsAdresStraat">
            <Input disabled />
          </Form.Item>
          <div className={styles.companyFieldPair}>
            <Form.Item label={t('form.companyInfo.houseNumber')} name="vestigingsAdresHuisnummer">
              <Input disabled />
            </Form.Item>
            <Form.Item label={t('form.companyInfo.addition')} name="vestigingsAdresHuisnummerToevoeging">
              <Input disabled />
            </Form.Item>
          </div>
          <div className={styles.companyFieldPair}>
            <Form.Item label={t('form.companyInfo.zipcode')} name="vestigingsAdresPostCodeCijfers">
              <Input disabled />
            </Form.Item>
            <Form.Item label=" " colon={false} name="vestigingsAdresPostCodeLetters">
              <Input disabled aria-label="Postcodeletters vestigingsadres" />
            </Form.Item>
          </div>
          <Form.Item label={t('form.companyInfo.city')} name="vestigingsAdresPlaats">
            <Input disabled />
          </Form.Item>
          {requiresTotaleBeteelbareKasOppervlakte && (
            <Form.Item
              label={
                <span>
                  {t('form.companyInfo.totaleBeteelbareKasOppervlakte')}{' '}
                  <Tooltip
                    title={
                      <>
                        <p>{t('form.companyInfo.totaleBeteelbareKasOppervlakteTooltip')}</p>
                        <p><strong>{t('form.companyInfo.beteelbaarOppervlakKenmerken')}</strong></p>
                        <ul>
                          <li>{t('form.companyInfo.beteelbaarOppervlakDefinitie')}</li>
                          <li>{t('form.companyInfo.beteelbaarOppervlakRegistratiejaar')}</li>
                          <li>{t('form.companyInfo.beteelbaarOppervlakVerhouding')}</li>
                          <li>
                            {t('form.companyInfo.beteelbaarOppervlakVoorbeelden')}
                            <ul>
                              <li>{t('form.companyInfo.beteelbaarOppervlakEnkeleTeeltlaag')}</li>
                              <li>{t('form.companyInfo.beteelbaarOppervlakMeerdereTeeltlagen')}</li>
                            </ul>
                          </li>
                        </ul>
                      </>
                    }
                    trigger={['hover', 'focus', 'click']}
                    placement="right"
                    classNames={{ root: styles.infoTooltip }}>
                    <InfoCircleOutlined tabIndex={0} aria-label="Toelichting kasoppervlakte" />
                  </Tooltip>
                </span>
              }
              name="totaleBeteelbareKasOppervlakte"
              rules={[
                { required: true, message: 'Vul de totale beteelbare kasoppervlakte in.' },
                { type: 'number', min: 1, message: 'Waarde mag niet 0 zijn' },
              ]}>
              <InputNumber step={1} min={0} max={999999999} addonAfter="m²" style={{ width: '100%' }} />
            </Form.Item>
          )}
          {isBagEnabled && (
            <div className={styles.companyBagField}>
              <Form.Item label={t('form.companyInfo.bagKoppeling')} name="pandId" rules={[{ required: true, message: 'Koppel je bedrijf aan een pand.' }]}>
                <Input readOnly />
              </Form.Item>
              <Tooltip title="Pand selecteren of wijzigen">
                <Button icon={<EditOutlined />} aria-label="Pand selecteren of wijzigen" onClick={() => setModalIsShown(true)} />
              </Tooltip>
            </div>
          )}
        </Col>
        <Col xs={24} lg={8}>
          <h2>Postadres</h2>
          <Form.Item label={t('form.companyInfo.postalAddress')} name="postAdresStraat" rules={[{ required: true, message: 'Vul de straat in.' }]}>
            <Input />
          </Form.Item>
          <div className={styles.companyFieldPair}>
            <Form.Item label={t('form.companyInfo.houseNumber')} name="postAdresHuisnummer" rules={[{ required: true, message: 'Vul het huisnummer in.' }]}>
              <Input />
            </Form.Item>
            <Form.Item label={t('form.companyInfo.addition')} name="postAdresHuisnummerToevoeging">
              <Input />
            </Form.Item>
          </div>
          <div className={styles.companyFieldPair}>
            <Form.Item
              label={t('form.companyInfo.zipcode')}
              name="postAdresPostCodeCijfers"
              rules={[{ required: true, pattern: /^\d{4}$/, message: 'Vul vier cijfers in.' }]}>
              <Input maxLength={4} inputMode="numeric" />
            </Form.Item>
            <Form.Item
              label=" "
              colon={false}
              name="postAdresPostCodeLetters"
              rules={[{ required: true, pattern: /^[a-zA-Z]{2}$/, message: 'Vul twee letters in.' }]}>
              <Input maxLength={2} aria-label="Postcodeletters postadres" />
            </Form.Item>
          </div>
          <Form.Item label={t('form.companyInfo.city')} name="postAdresPlaats" rules={[{ required: true, message: 'Vul de plaats in.' }]}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} lg={8}>
          <h2>Contactpersoon</h2>
          <Form.Item label={t('form.companyInfo.contactPerson')} name="contactpersoon" rules={[{ required: true, message: 'Vul de contactpersoon in.' }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('form.companyInfo.phoneNumber')} name="telefoon" rules={[{ required: true, message: 'Vul het telefoonnummer in.' }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('form.companyInfo.fax')} name="fax">
            <Input />
          </Form.Item>
          <Form.Item label={t('form.companyInfo.email')} name="email" rules={[{ required: true, message: 'Vul het e-mailadres in.' }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('form.companyInfo.url')} name="internet">
            <Input />
          </Form.Item>
        </Col>
      </Row>
      {showOwnershipStatus && (
        <div className={styles.companyOwnership}>
          <Form.Item label={t('form.companyInfo.ownership.question')} name="eigendomstypeDeelnemer">
            <Radio.Group>
              <Radio value={OwnershipType.OwnCompany}>{t('form.companyInfo.ownership.option_1')}</Radio>
              <Radio value={OwnershipType.GroupCompany}>{t('form.companyInfo.ownership.option_2')}</Radio>
              <Radio value={OwnershipType.ThirdPartyLease}>{t('form.companyInfo.ownership.option_3')}</Radio>
            </Radio.Group>
          </Form.Item>
        </div>
      )}
      {isBagEnabled && (
        <Modal title="Bedrijf koppelen aan pand" open={modalIsShown} onCancel={() => setModalIsShown(false)} width="80%" footer={null} destroyOnHidden>
          <BagMap modalIsShown={modalIsShown} setModalIsShown={setModalIsShown} />
        </Modal>
      )}
    </div>
  );
};
